import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBars,
  faHouse,
  faClock,
  faChartBar,
  faWifi,
  faCloud,
  faRightFromBracket,
  faCircleQuestion,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { chFetch, chNetejarSessio } from "./chAuth";
import { chComptarPendents } from "./chIndexedDB";
import { chSincronitzarPendents } from "./chSync";
import { CH_ESTIL_FONS } from "./chEstil";
import { useChEstatConnexio } from "./chConnexio";

const ETIQUETA_ESTAT_CONNEXIO = {
  CONNECTAT: "Connectat",
  SENSE_XARXA: "Sense xarxa",
};

const ICONA_ESTAT_CONNEXIO = {
  CONNECTAT: faWifi,
  SENSE_XARXA: faCloud,
};

// Capçalera + menú + navegació inferior comuns a totes les pantalles
// autenticades del portal (CURRENT_TASK.md §5.3: logo, fons subtil, colors
// corporatius). El fons Fons.jpg es tracta com a decoració de la pàgina,
// no com una imatge fixa a reproduir píxel a píxel.
//
// També centralitza el disparador de sincronització automàtica (§13: en
// muntar-se, en tornar la connexió, en tornar a primer pla) i el badge de
// pendents, ja que aquest component es munta a totes les pantalles privades.
//
// La prop `ajuda` (opcional) identifica el tema d'ajuda contextual de la
// pantalla actual ("inici", "tipusPausa", "historic", "registres");
// quan s'indica, es mostra el botó "?" que obre ControlHorariAjuda amb el
// contingut corresponent.
function ControlHorariLayout({ titol = "Control Horari", ajuda = null, children }) {
  const [menuObert, setMenuObert] = useState(false);
  const { estat: estatConnexio, connectat } = useChEstatConnexio();
  const [pendents, setPendents] = useState(0);
  const navigate = useNavigate();

  const actualitzarComptadorPendents = async () => {
    setPendents(await chComptarPendents());
  };

  useEffect(() => {
    actualitzarComptadorPendents();
    chSincronitzarPendents().then(actualitzarComptadorPendents);

    const alCanviarPendents = () => actualitzarComptadorPendents();
    window.addEventListener("ch-pendents-canvi", alCanviarPendents);
    return () => window.removeEventListener("ch-pendents-canvi", alCanviarPendents);
    // Només en muntar aquesta instància del layout (una per pantalla).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const alTornarVisible = () => {
      if (document.visibilityState === "visible") {
        chSincronitzarPendents().then(actualitzarComptadorPendents);
      }
    };
    window.addEventListener("online", alTornarVisible);
    document.addEventListener("visibilitychange", alTornarVisible);
    return () => {
      window.removeEventListener("online", alTornarVisible);
      document.removeEventListener("visibilitychange", alTornarVisible);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tancarSessio = async () => {
    setMenuObert(false);
    try {
      const refreshToken = localStorage.getItem("ch_refreshToken");
      if (refreshToken) {
        await chFetch("/ControlHorariLogout.php", {
          method: "POST",
          body: JSON.stringify({ refreshToken }),
        });
      }
    } catch {
      // Encara que la revocació falli (per exemple, sense connexió), es
      // netegen igualment les credencials locals: CURRENT_TASK.md §41.
    } finally {
      chNetejarSessio();
      navigate("/Control_Horari/login", { replace: true });
    }
  };

  return (
    <div className="ch-app" style={CH_ESTIL_FONS}>
      <header className="ch-header">
        <button
          type="button"
          className="ch-boto-icona"
          onClick={() => setMenuObert(true)}
          aria-label="Obrir menú"
        >
          <FontAwesomeIcon icon={faBars} />
        </button>

        <div className="ch-header-titol">
          <img src="/images/controlhorari/logo-punt.png" alt="" className="ch-logo" />
          <span>{titol}</span>
        </div>

        <div className="ch-header-dreta">
          <div className={`ch-estat-connexio ${connectat ? "ch-online" : "ch-offline"}`}>
            <FontAwesomeIcon icon={ICONA_ESTAT_CONNEXIO[estatConnexio]} />
            <span>{ETIQUETA_ESTAT_CONNEXIO[estatConnexio]}</span>
            {pendents > 0 && <span className="ch-badge-pendents">{pendents}</span>}
          </div>
          {ajuda && (
            <button
              type="button"
              className="ch-boto-icona"
              onClick={() => navigate(`/Control_Horari/ajuda/${ajuda}`)}
              aria-label="Ajuda"
            >
              <FontAwesomeIcon icon={faCircleQuestion} />
            </button>
          )}
        </div>
      </header>

      {menuObert && (
        <div className="ch-menu-overlay" onClick={() => setMenuObert(false)}>
          <div className="ch-menu" onClick={(e) => e.stopPropagation()}>
            <div className="ch-menu-capcalera">
              <span>Menú</span>
              <button
                type="button"
                className="ch-boto-icona"
                onClick={() => setMenuObert(false)}
                aria-label="Tancar menú"
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <button
              type="button"
              className="ch-menu-item"
              onClick={() => {
                setMenuObert(false);
                navigate("/Control_Horari/ajuda/inici");
              }}
            >
              <FontAwesomeIcon icon={faCircleQuestion} />
              <span>Ajuda</span>
            </button>
            <button type="button" className="ch-menu-item ch-menu-item-perill" onClick={tancarSessio}>
              <FontAwesomeIcon icon={faRightFromBracket} />
              <span>Tancar sessió</span>
            </button>
          </div>
        </div>
      )}

      <main className="ch-contingut">{children}</main>

      <nav className="ch-nav-inferior">
        <NavLink
          to="/Control_Horari/avui"
          className={({ isActive }) => `ch-nav-item${isActive ? " actiu" : ""}`}
        >
          <FontAwesomeIcon icon={faHouse} />
          <span>Inici</span>
        </NavLink>
        <NavLink
          to="/Control_Horari/historic"
          className={({ isActive }) => `ch-nav-item${isActive ? " actiu" : ""}`}
        >
          <FontAwesomeIcon icon={faClock} />
          <span>Històric</span>
        </NavLink>
        <NavLink
          to="/Control_Horari/registres"
          className={({ isActive }) => `ch-nav-item${isActive ? " actiu" : ""}`}
        >
          <FontAwesomeIcon icon={faChartBar} />
          <span>Registres</span>
        </NavLink>
      </nav>
    </div>
  );
}

export default ControlHorariLayout;
